import * as DialogPrimitive from '@radix-ui/react-dialog';

import { DialogOverlay, DialogPortal, DialogTitle } from './Dialog';
import { cn } from './utils';


/**
 * Bottom-anchored modal sheet — the phone home for selects / filters / pickers
 * per DESIGN.md `bottom-sheet` (surface-1, `16 16 0 0` radius, hairline-strong
 * grabber, `bg-scrim` backdrop) and the .pen "M Bulk Actions" frames
 * (m1qXa7 dark / VICSM light, Sheet fIlaz / vgADt).
 *
 * `Drawer.jsx` is right-anchored only (`inset-y-0 right-0`, no placement prop)
 * and hand-rolls its own scrim/ESC handling, so a bottom sheet is NOT a Drawer
 * variant. This builds on @radix-ui/react-dialog — the same primitive
 * `Dialog.jsx` uses — so focus trap, ESC, scroll-lock and the `aria-modal`
 * wiring come from the library, and it reuses `DialogOverlay`/`DialogPortal`
 * rather than re-declaring a scrim.
 *
 * The sheet has NO horizontal padding (matching the .pen, whose Sheet frame is
 * `padding [10,0,16,0]`) so full-bleed action rows work; children own their own
 * side padding. Bottom padding respects the iOS safe area.
 *
 *   <BottomSheet open={open} onOpenChange={setOpen} title="Filters">
 *     <div className="px-4">…</div>
 *   </BottomSheet>
 */
function BottomSheet({ open, onOpenChange, title, children, className }) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />

        <DialogPrimitive.Content
          className={cn(
            'fixed inset-x-0 bottom-0 z-50 flex max-h-[85svh] flex-col outline-none',
            'rounded-t-xl border border-hairline bg-surface-1 pt-2.5',
            'pb-[max(16px,env(safe-area-inset-bottom))]',
            'data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom',
            'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom',
            className,
          )}
          // Children are arbitrary, so there is no DialogDescription to point at;
          // opting out keeps Radix from warning about a missing description.
          aria-describedby={undefined}
        >
          {/* Grabber (.pen GW/Grab: 40×4, radius pill, hairline-strong). */}
          <div className="flex shrink-0 justify-center pb-2">
            <span aria-hidden="true" className="h-1 w-10 rounded-full bg-hairline-strong" />
          </div>

          {/* Radix requires a Title for the accessible name; an untitled sheet
              still gets one, visually hidden. */}
          <DialogTitle
            className={cn(
              'shrink-0 px-4 pb-2 text-center text-card-title font-semibold text-ink',
              title ? null : 'sr-only',
            )}
          >
            {title || 'Sheet'}
          </DialogTitle>

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            {children}
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogPrimitive.Root>
  );
}

BottomSheet.displayName = 'BottomSheet';
export { BottomSheet };
export default BottomSheet;
